import { type EntryIdInput, formatEntryId } from "./entryId";
import { createAction, type MutationAction } from "./mutationAction";
import {
	type ActionDeps,
	ApiError,
	actingHeaders,
	apiCall,
	plural,
	refreshEntries,
} from "./types";

/**
 * Restore soft-deleted entries back to the working set.
 *
 * The v2 PATCH endpoint accepts `deleted → tracked`; restoring to `tracked`
 * keeps the operation unambiguous and lets users review/export afterward.
 */
export function restoreDeletedAction(
	ids: EntryIdInput[],
	deps: ActionDeps,
	timekeeperId?: string | null,
): MutationAction {
	const wireIds = ids.map(formatEntryId);
	const v2IdSet = new Set(wireIds);
	const rollback = deps.updateEntries((prev) =>
		prev.map((entry) =>
			v2IdSet.has(entry.id) ? { ...entry, status: "tracked" } : entry,
		),
	);
	const n = ids.length;

	return createAction({
		execute: async () => {
			const failures: { id: EntryIdInput; err: unknown }[] = [];
			for (const id of ids) {
				try {
					await apiCall(deps, {
						endpoint: `/v2/entries/${formatEntryId(id)}`,
						method: "PATCH",
						body: { status: "tracked" },
						headers: actingHeaders(timekeeperId),
					});
				} catch (err) {
					failures.push({ id, err });
				}
			}
			if (failures.length > 0) {
				const firstMsg =
					failures[0].err instanceof Error
						? failures[0].err.message
						: "Request failed";
				throw Object.assign(
					new Error(
						`${failures.length} of ${n} ${plural(n)} failed: ${firstMsg}`,
					),
					{ failures },
				);
			}
		},
		rollback,
		toast: {
			loading: `Restoring ${n} ${plural(n)}…`,
			success: `Restored ${n} ${plural(n)}`,
			error: (err) => {
				if (err instanceof ApiError) {
					if (err.code === "ENTRY_EXPORTED")
						return "Exported entries can't be changed";
					if (err.code === "ENTRY_NOT_FOUND")
						return "One or more entries no longer exist";
				}
				return "Failed to restore entries";
			},
		},
		onSuccess: () => refreshEntries(deps),
		onError: () => refreshEntries(deps),
	});
}
